/**
 * LessonLink - つまだけ（要約読解訓練）モジュール
 */
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import { showMessage, clearMessage } from './utils';
import { getCurrentUserEmail } from './settings';

type SummaryClient = ReturnType<typeof generateClient<Schema>>;
let client: SummaryClient;

function getClient() {
  if (!client) {
    client = generateClient<Schema>();
  }
  return client;
}

// 難易度ノブ（守井が月次レビューで生徒の様子を見て手動調整する）
const DIFFICULTY_FACT_COUNT = 3;
const DIFFICULTY_MAX_LENGTH = 150;

// 1日の回数上限
const DAILY_LIMIT = 5;
const USAGE_TTL_DAYS = 2;
const RECORD_TTL_DAYS = 90;

let currentPassageText = '';
let currentFactList: string[] = [];
let currentTheme = '';

export function initSummaryTraining() {
  const genBtn = document.getElementById('summary-gen-btn') as HTMLButtonElement;
  const genBtnBottom = document.getElementById('summary-gen-btn-bottom') as HTMLButtonElement;
  const gradeBtn = document.getElementById('summary-grade-btn') as HTMLButtonElement;

  genBtn.addEventListener('click', async () => {
    await handleGenerateProblem();
  });

  genBtnBottom.addEventListener('click', async () => {
    await handleGenerateProblem();
  });

  gradeBtn.addEventListener('click', async () => {
    await handleSubmitSummary();
  });
}

function todayString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

async function getTodayUsageCount(studentOwner: string): Promise<number> {
  const date = todayString();
  const { data } = await getClient().models.ReadingSummaryUsage.get({ studentOwner, date });
  return data?.count ?? 0;
}

async function incrementTodayUsage(studentOwner: string): Promise<void> {
  const date = todayString();
  const { data: existing } = await getClient().models.ReadingSummaryUsage.get({ studentOwner, date });
  const expiresAt = Math.floor(Date.now() / 1000) + USAGE_TTL_DAYS * 24 * 60 * 60;

  if (existing) {
    await getClient().models.ReadingSummaryUsage.update({
      studentOwner,
      date,
      count: existing.count + 1,
      expiresAt,
    });
  } else {
    await getClient().models.ReadingSummaryUsage.create({
      studentOwner,
      date,
      count: 1,
      expiresAt,
    });
  }
}

async function handleGenerateProblem() {
  clearMessage();

  const studentOwner = getCurrentUserEmail();
  if (!studentOwner) {
    showMessage('ログイン情報が確認できませんでした。');
    return;
  }

  const usageEl = document.getElementById('summary-usage-text') as HTMLElement;
  const passageEl = document.getElementById('summary-passage-text') as HTMLElement;
  const passageArea = document.getElementById('summary-passage-area') as HTMLElement;
  const answerArea = document.getElementById('summary-answer-area') as HTMLElement;
  const feedbackArea = document.getElementById('summary-feedback-area') as HTMLElement;
  const summaryInput = document.getElementById('summary-input') as HTMLTextAreaElement;
  const genBtn = document.getElementById('summary-gen-btn') as HTMLButtonElement;
  const genBtnBottom = document.getElementById('summary-gen-btn-bottom') as HTMLButtonElement;
  const gradeBtn = document.getElementById('summary-grade-btn') as HTMLButtonElement;

  let usedCount: number;
  try {
    usedCount = await getTodayUsageCount(studentOwner);
  } catch (error) {
    console.error('getTodayUsageCount error:', error);
    showMessage('通信に失敗しました。もう一度お試しください。');
    return;
  }

  if (usedCount >= DAILY_LIMIT) {
    showMessage('今日はもうたくさん頑張ったね。また明日やろう！');
    return;
  }

  summaryInput.value = '';
  feedbackArea.classList.add('hidden');
  gradeBtn.disabled = false;

  passageEl.textContent = '問題作成中...';
  passageArea.classList.remove('hidden');
  answerArea.classList.add('hidden');
  genBtn.disabled = true;
  genBtnBottom.disabled = true;

  try {
    const result = await getClient().mutations.generateSummaryProblem({
      factCount: DIFFICULTY_FACT_COUNT,
      maxLength: DIFFICULTY_MAX_LENGTH,
    });
    const res = result.data;

    if (!res || !res.success || !res.passageText || !res.factList) {
      showMessage(res?.errorMessage || '問題の生成に失敗しました。もう一度お試しください。');
      passageArea.classList.add('hidden');
      return;
    }

    currentPassageText = res.passageText;
    currentFactList = res.factList.filter((f): f is string => !!f);
    currentTheme = res.theme || '';

    passageEl.textContent = currentPassageText;
    answerArea.classList.remove('hidden');

    // 利用回数の記録に失敗しても、既に表示した問題文はそのまま使わせる（記録はベストエフォート）
    try {
      await incrementTodayUsage(studentOwner);
      const remaining = DAILY_LIMIT - (usedCount + 1);
      usageEl.textContent = `今日の残り ${Math.max(remaining, 0)} 回`;
    } catch (error) {
      console.error('incrementTodayUsage error:', error);
    }

    passageEl.setAttribute('tabindex', '-1');
    passageEl.focus();
  } catch (error) {
    console.error('generateSummaryProblem error:', error);
    showMessage('問題の生成に失敗しました。もう一度お試しください。');
    passageArea.classList.add('hidden');
  } finally {
    genBtn.disabled = false;
    genBtnBottom.disabled = false;
  }
}

async function handleSubmitSummary() {
  clearMessage();

  const summaryInput = document.getElementById('summary-input') as HTMLTextAreaElement;
  const feedbackArea = document.getElementById('summary-feedback-area') as HTMLElement;
  const feedbackText = document.getElementById('summary-feedback-text') as HTMLElement;
  const gradeBtn = document.getElementById('summary-grade-btn') as HTMLButtonElement;

  const userSummary = summaryInput.value.trim();

  if (!currentPassageText) {
    showMessage('先に「出題する」を押して文章を表示してください。');
    return;
  }

  if (!userSummary) {
    showMessage('「つまり」を入力してから採点してください。');
    return;
  }

  feedbackText.textContent = '採点中...';
  feedbackArea.classList.remove('hidden');
  gradeBtn.disabled = true;

  try {
    const result = await getClient().mutations.gradeSummaryAnswer({
      passageText: currentPassageText,
      factList: currentFactList,
      userSummary,
    });
    const res = result.data;

    if (!res || !res.success || !res.feedbackText) {
      showMessage(res?.errorMessage || '採点に失敗しました。もう一度お試しください。');
      feedbackArea.classList.add('hidden');
      gradeBtn.disabled = false;
      return;
    }

    feedbackText.textContent = res.feedbackText;
    feedbackText.focus();

    // 生徒には見せず、裏で記録（守井の月次レビュー用）
    const studentOwner = getCurrentUserEmail();
    if (studentOwner) {
      const expiresAt = Math.floor(Date.now() / 1000) + RECORD_TTL_DAYS * 24 * 60 * 60;
      await getClient().models.ReadingSummaryRecord.create({
        studentOwner,
        theme: currentTheme,
        passageText: currentPassageText,
        factList: currentFactList,
        summaryText: userSummary,
        factsCoveredCount: res.factsCoveredCount ?? 0,
        factsTotalCount: res.factsTotalCount ?? currentFactList.length,
        hallucinatedFacts: res.hallucinatedFacts || [],
        feedbackText: res.feedbackText,
        difficultyFactCount: DIFFICULTY_FACT_COUNT,
        difficultyMaxLength: DIFFICULTY_MAX_LENGTH,
        expiresAt,
      });
    }
  } catch (error) {
    console.error('gradeSummaryAnswer error:', error);
    showMessage('採点に失敗しました。もう一度お試しください。');
    feedbackArea.classList.add('hidden');
    gradeBtn.disabled = false;
  }
}
